import Translate from "lang/Translate";
import Pagination, { getPageCount, paginateFn } from "components/library/Pagination";
import "./scenarios.scss"
import SearchInput from "components/library/SearchInput";
import CaseListHeader from "./components/CaseListHeader";
import CaseListItem from "./components/CaseListItem";
import { BaseButton } from "components/library/buttons";
import DefaultModal from "components/library/DefaultModal";
import useToggle from "hooks/useToggle";
import CreateNew from "./components/CreateNew";
import Practice from "./practice";
import { ApiPaths } from "services/api/ApiPaths";
import { TScenario, TScenarios } from "./scenario.model";
import { loadScenarios } from "./services";
import useSWR from "swr";
import UserRoleValidator from "components/user/UserRoleValidator";
import LoadingSpinner from "components/library/loading-spinner/LoadingSpinner";
import { useMemo, useState } from "react";
import useSortScenarioList from "hooks/useSortScenarioList";
import SelectField from "components/library/input-fields/SelectField";
import { Province } from "utils/constants";
import CaseNoResults from "./components/CaseNoResults";
import { TOption, TOptionMixHtmlEvt } from "components/library/input-fields/BaseSelect";
import { RootState } from "store";
import { useSelector } from "react-redux";

const Scenarios = () => {
    const ITEMS_PER_PAGE = 10
    const [searchEle, setSearchEle] = useState("")
    const [province, setProvince] = useState<string | null>(null)
    const [level, setLevel] = useState<string | null>(null)
    const [activePageNo, setActivePageNo] = useState(0)
    const [isOpen, toggle] = useToggle()
     const userRole = useSelector((state: RootState) => state.user.userSelectedRole);
    const { isLoading, data: scenarios = [], mutate } = useSWR<TScenarios>([ApiPaths.SCENARIO, userRole], () => loadScenarios(userRole|| ""))
    const { handleSortToggle, sortIndex, sortList, sortBy } = useSortScenarioList()
    const [editScenario, setEditScenario] = useState(false);
    const [dataToEdit, setDataToEdit] = useState<TScenario | null>(null);

    const levelOptions = useMemo((): TOption[] => {
        const optValues: string[] = Array.from(new Set(scenarios?.map(o => o.level)))
        return optValues?.map(opt => {
            return {
                value: opt,
                label: opt
            }
        })?.sort((a,b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
    }, [scenarios])

    if(level && !levelOptions?.find(opt => opt.value === level)) setLevel(null)

    const listItems = (() => {
        //search funnel
        const searchFunnel = scenarios?.filter(d => {
            const searchKey = searchEle.toLowerCase()
            const searchable = [d.name, d.frenchName, d.province, d.taxpayerName, d.casestudyNumber, d.level]
            return searchable.find(e => e?.toLowerCase().includes(searchKey))
        })

        //filter funnel
        const filterFunnel = searchFunnel.filter(p => {
            if (province) return p.province === province
            return true
        }).filter(l => {
            if (level) return l.level === level
            return true
        })

        //pagination and sort
        const paginatedResult = paginateFn<TScenario>(filterFunnel, ITEMS_PER_PAGE)[activePageNo]
        const sortedResult = sortList(paginatedResult)

        return {
            render: sortedResult ?? [], // per page sorted
            result: filterFunnel // final items available through funnel
        }
    })()

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setActivePageNo(0)
        setSearchEle(e.target.value)
    }

    const handleLevelChange = (e: TOptionMixHtmlEvt) => {
        setActivePageNo(0)
        setLevel(e.value ? e.value : null)
    }

    const handleProvinceChange = (e: TOptionMixHtmlEvt) => {
        setActivePageNo(0)
        setProvince(e.value ? e.value : null)
    }
    const openEditMode = (data: TScenario) => {
        setEditScenario(true);
        setDataToEdit(data);
        toggle();

    }

    const closeModal = () => {
        setEditScenario(false);
        setDataToEdit(null);
        toggle(false);
    }

    return (
        <div data-testid="scenarios-entry" className="main-container flex-center is-flex-direction-column">
            <div className="w-100 mb-3 flex-center is-justify-content-flex-end">
                <UserRoleValidator validatorName="CREATE_SCENARIO">
                    <BaseButton buttonStyles="is-primary" fieldId="" labelKey="Buttons.create_new_scenario" onClick={() => toggle()} />
                </UserRoleValidator>
                <UserRoleValidator validatorName="SHOW_PRACTICE">
                    <Practice />
                </UserRoleValidator>
            </div>
            <div className="scenarios-container border w-100">
                <div className="border-bottom p-4">
                    <Translate className="is-size-4 has-text-weight-bold" text="Titles.scenarios" />
                </div>
                <div className="filter-container border-bottom p-4">
                    <SearchInput className="search-box pr-3" onChangeHandler={handleSearchInputChange} />
                    <SelectField className="select-filter mr-3" fieldId="province" selectedValue={province} onChange={handleProvinceChange} options={Province} placeholder="Labels.filter_province" showClearSelection />
                    <SelectField className="select-filter " fieldId="level" selectedValue={level} onChange={handleLevelChange} options={levelOptions} placeholder="Labels.filter_level" showClearSelection />
                </div>
                <CaseListHeader handleSortToggle={handleSortToggle} sortIndex={sortIndex} sortBy={sortBy}/>
                {isLoading ?
                    <LoadingSpinner /> :
                    listItems?.render?.length ?
                        listItems?.render?.map(d => <CaseListItem key={d.id} data={d} reload={mutate} setEditMode={openEditMode} />) :
                        <CaseNoResults />
                }
                <div className="border-bottom p-4 flex-center">
                    <Pagination pages={getPageCount(listItems?.result, ITEMS_PER_PAGE)} page={activePageNo} onPageChange={(n) => setActivePageNo(n)} />
                </div>
            </div>
            <DefaultModal isOpen={isOpen} handleCloseModal={() => closeModal()}>
                <CreateNew reload={mutate} editMode ={editScenario} editData= {dataToEdit} />
            </DefaultModal>
        </div>
    )
}

export default Scenarios;